import { gql } from 'graphql-request';

/**
 * The portfolio species queries, served over the global H3 prediction grid.
 *
 * The grid has no per-species timestamps, so there is no "Added" column and no
 * New/Updated badges. `snapshot` is the provenance of each answer.
 *
 * The Red List set is CR/EN/VU/NT and non-invasive. The threatened subset
 * (CR/EN/VU) is reported separately: NT is on the Red List but is not threatened.
 */

export type PortfolioSpeciesIucnCount = {
    category: string;
    count: number;
};

export type PortfolioSiteRef = {
    siteId: string;
    siteName: string;
};

export type PortfolioSpeciesSummary = {
    snapshot: string;
    /** redListSpeciesCount + invasiveSpeciesCount, as the current page defines it. */
    totalSpeciesCount: number;
    siteCount: number;
    redListSpeciesCount: number;
    redListSiteCount: number;
    /** The threatened subset of redListSpeciesCount: CR + EN + VU. */
    threatenedSpeciesCount: number;
    threatenedSiteCount: number;
    invasiveSpeciesCount: number;
    invasiveSiteCount: number;
    iucn: PortfolioSpeciesIucnCount[];
    sitesWithoutGeometry: PortfolioSiteRef[];
};

export type PortfolioSpeciesListItem = {
    scientificName: string;
    commonName: string | null;
    iucnRedListCategory: string;
    confidence: string;
    /** Sites predicting the species at all — presence, not list membership. */
    siteCount: number;
    /** First appeared in the served snapshot — new until the next monthly roll (DD-6012). */
    isNew: boolean;
    /**
     * Sites where the species is invasive / counts on the Red List (DD-5796).
     * Null when the server predates the figures: unknown, never zero.
     */
    invasiveSiteCount: number | null;
    redListSiteCount: number | null;
};

export type PortfolioSpeciesListResponse = {
    snapshot: string;
    species: PortfolioSpeciesListItem[];
    pagination: {
        page: number;
        pageSize: number;
        total: number;
    };
};

export const portfolioSpeciesSummaryQuery = gql`
    query getPortfolioSpeciesSummary($customerId: String!, $filters: [CustomFilterOptionInput]) {
        portfolio {
            getPortfolioSpeciesSummary(customerId: $customerId, filters: $filters) {
                snapshot
                totalSpeciesCount
                siteCount
                redListSpeciesCount
                redListSiteCount
                threatenedSpeciesCount
                threatenedSiteCount
                invasiveSpeciesCount
                invasiveSiteCount
                iucn {
                    category
                    count
                }
                sitesWithoutGeometry {
                    siteId
                    siteName
                }
            }
        }
    }
`;

const speciesListFields = `
    snapshot
    species {
        scientificName
        commonName
        iucnRedListCategory
        confidence
        siteCount
        isNew
        invasiveSiteCount
        redListSiteCount
    }
    pagination {
        page
        pageSize
        total
    }
`;

export const portfolioRedListSpeciesQuery = gql`
    query getPortfolioRedListSpecies(
        $customerId: String!
        $filters: [CustomFilterOptionInput]
        $pagination: PaginationInput!
        $sortBy: SortByInput
        $searchText: String
    ) {
        portfolio {
            getPortfolioRedListSpecies(
                customerId: $customerId
                filters: $filters
                pagination: $pagination
                sortBy: $sortBy
                searchText: $searchText
            ) {
                ${speciesListFields}
            }
        }
    }
`;

export const portfolioInvasiveSpeciesQuery = gql`
    query getPortfolioInvasiveSpecies(
        $customerId: String!
        $filters: [CustomFilterOptionInput]
        $pagination: PaginationInput!
        $sortBy: SortByInput
        $searchText: String
    ) {
        portfolio {
            getPortfolioInvasiveSpecies(
                customerId: $customerId
                filters: $filters
                pagination: $pagination
                sortBy: $sortBy
                searchText: $searchText
            ) {
                ${speciesListFields}
            }
        }
    }
`;

export type PortfolioSpeciesSummaryQueryResult = {
    portfolio: {
        getPortfolioSpeciesSummary: PortfolioSpeciesSummary;
    };
};

export type PortfolioRedListSpeciesQueryResult = {
    portfolio: {
        getPortfolioRedListSpecies: PortfolioSpeciesListResponse;
    };
};

export type PortfolioInvasiveSpeciesQueryResult = {
    portfolio: {
        getPortfolioInvasiveSpecies: PortfolioSpeciesListResponse;
    };
};

// The grid-backed species detail behind the species info modal: which of the
// filtered sites predict one species, and how. Detection is composed server-side
// from Neo4j, so this one response carries both halves.
export const portfolioSpeciesDetailQuery = gql`
    query getPortfolioSpeciesDetail($customerId: String!, $scientificName: String!, $filters: [CustomFilterOptionInput]) {
        portfolio {
            getPortfolioSpeciesDetail(customerId: $customerId, scientificName: $scientificName, filters: $filters) {
                snapshot
                scientificName
                sites {
                    siteId
                    siteName
                    country
                    confidence
                    invasive
                    hasEDNAData
                    hasUploadedData
                    foundInEDNA
                    foundInUploadedData
                }
            }
        }
    }
`;

export type PortfolioSpeciesDetailSite = {
    siteId: string;
    siteName: string;
    country: string;
    confidence: string;
    invasive: boolean;
    hasEDNAData: boolean;
    hasUploadedData: boolean;
    foundInEDNA: boolean;
    foundInUploadedData: boolean;
};

export type PortfolioSpeciesDetail = {
    snapshot: string;
    scientificName: string;
    sites: PortfolioSpeciesDetailSite[];
};

export type PortfolioSpeciesDetailQueryResult = {
    portfolio: {
        getPortfolioSpeciesDetail: PortfolioSpeciesDetail;
    };
};
